import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Send, ShieldAlert, ShieldCheck } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useViewport } from "@/ds";
import { useHideTabBar } from "@/hooks/useHideTabBar";
import useVisualViewport from "@/hooks/useVisualViewport";
import VerifyEmailBanner from "@/components/auth/VerifyEmailBanner";
import { formatTime } from "@/lib/dates";

/**
 * How tall the composer is allowed to get before it scrolls inside itself.
 *
 * Five lines (T17-M1b, Jorge), measured off the field's own computed
 * line-height rather than a hard pixel ceiling, because the body font is fluid
 * and a number that is five lines on a laptop is three on a phone.
 */
const COMPOSER_MAX_LINES = 5;

/**
 * Fit the field to what is written in it, up to the ceiling.
 *
 * `height: auto` first is not optional: `scrollHeight` of an element with an
 * explicit height is that height, so without the reset the box can only ever
 * grow and never shrink back when text is deleted.
 *
 * The border is added by hand. Tailwind's preflight puts every element in
 * `border-box`, where `scrollHeight` counts padding but not border while the
 * `height` being assigned counts both. Since T17-M1c the field itself has no
 * border — the wrapper that paints focus carries it — so the term is zero
 * today and stays because the day it is not, a one-line field scrolls by one
 * line and nobody looks here.
 */
function fitComposer(el) {
  if (!el) return;
  el.style.height = "auto";
  const cs = window.getComputedStyle(el);
  const line = parseFloat(cs.lineHeight) || 20;
  const border =
    (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  const padding =
    (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const max = line * COMPOSER_MAX_LINES + padding + border;
  el.style.height = `${Math.min(el.scrollHeight + border, max)}px`;
}

export default function Chat() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.profile?.role;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * Below 768px this page is two screens, not two panes (#16).
   *
   * The split layout put a 260px list and a conversation side by side inside
   * 60vh of phone, which is roughly four messages tall with a keyboard open.
   * The native pattern instead — WhatsApp's, and every messenger's — is a
   * full-screen list that a tap replaces with a full-screen conversation, and
   * back returns to the list. Desktop keeps the split; nothing below is a new
   * route, only which of the two existing panes is on screen.
   */
  const { isMobile } = useViewport();
  const threadParam = searchParams.get("thread");
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/chat/threads");
    const list = data.threads || [];
    setThreads(list);
    // The most recent thread, and only when nothing was asked for by name: a
    // usable `?thread=` is opened by the effect below instead, which is the one
    // that can also catch it CHANGING. loadThread fetches the messages and
    // clears the unread count for whichever one ends up open.
    //
    // Not on a phone, where the list IS the screen: opening the newest
    // conversation there would mean a push-free visit to Messages always
    // landing inside somebody's chat, and the reader having to back out of a
    // conversation they never asked for.
    if (!isMobile && !active && list.length &&
        !list.some((th) => th.thread_id === threadParam)) {
      loadThread(list[0].thread_id);
    }
  };

  const loadThread = async (tid) => {
    const { data } = await api.get(`/chat/threads/${tid}`);
    setActive(tid);
    setThreads((prev) => prev.map((th) => th.thread_id === tid ? { ...th, ...data, unread_count: 0 } : th));
    // That GET also marked this thread's messages AND its bell notifications
    // read on the server. Both counters poll every 45s and live in other
    // branches of the tree, so without this they keep a badge for a message the
    // reader is looking at — most visibly when a push deep link lands here
    // without passing through either of them. An event rather than shared
    // state: one listener each, no context.
    //
    // Two events since T14-M2, because there are two channels now and this GET
    // moved both (hooks/unreadCounts.js).
    window.dispatchEvent(new CustomEvent("fixy:notifications-changed"));
    window.dispatchEvent(new CustomEvent("fixy:messages-changed"));
  };

  useEffect(() => { loadThreads(); }, []); // eslint-disable-line

  /**
   * Open the thread the URL names, including when the URL changes under us.
   *
   * `?thread=` used to be read once, inside the mount-time load, and only when
   * nothing was open yet. That is correct for arriving from elsewhere and inert
   * for the case Daniel reported on 2026-09-09: the bell links to THIS route
   * with a different thread, so clicking a chat notification while the chat is
   * already on screen is a same-route navigation. React Router updates the
   * search params without remounting, no effect was watching them, and the
   * panel closed over a conversation that had not moved.
   *
   * Spent once per NAVIGATION, not once per value. `location.key` is new on
   * every push even to an identical URL, which is what lets the bell reopen a
   * thread the reader had navigated away from inside the page — the param did
   * not change, so a ref holding the param would have called that a repeat and
   * done nothing. It is deliberately not keyed on `active`: the two are allowed
   * to disagree, because picking another conversation in the sidebar changes
   * `active` and leaves the URL alone, and an effect comparing them would drag
   * the reader straight back.
   */
  const honoredNav = useRef(null);
  useEffect(() => {
    if (!threadParam || honoredNav.current === location.key) return;
    if (!threads.some((th) => th.thread_id === threadParam)) return;
    honoredNav.current = location.key;
    if (threadParam !== active) loadThread(threadParam);
  }, [threadParam, threads, location.key]); // eslint-disable-line

  /**
   * Put the conversation back at its bottom.
   *
   * Named rather than inline because two different things ask for it now: a
   * render, below, which is how a newly arrived message comes into view — and
   * the keyboard, through `useVisualViewport`, which is T16 M4. Until that
   * second caller existed, re-anchoring happened ONLY as a side effect of
   * React rendering, so taking focus — which sets no state — left the screen
   * exactly as the keyboard had found it and the first keystroke was what put
   * it right.
   */
  const anchorToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(anchorToBottom);

  // Re-measured on every change rather than on keystrokes: a send clears `text`
  // without a keystroke, and the box has to come back down with it.
  useEffect(() => { fitComposer(inputRef.current); }, [text, active]);

  /**
   * Poll the open thread so incoming messages appear without reopening it.
   * (Chat real-time is out of MVP; polling is the accepted UX.) Pauses while
   * the tab is hidden and stops when the thread changes or the page unmounts.
   *
   * **This poll is also the presence heartbeat** (N13). The server reads this
   * request as "they are looking at this conversation now" and stops pushing
   * at somebody who is reading the message on screen
   * (`backend/services/presence.py`). Nothing extra is sent: the signal is
   * this request, which was already in flight, and the two conditions that
   * made it the right one were already true — it stops when the conversation
   * closes, and it skips while the tab is hidden.
   *
   * What N13 adds is the `visibilitychange` listener. Skipping a tick while
   * hidden was enough to let presence lapse, which is correct; coming BACK
   * used to wait out the rest of the interval, so for up to seven seconds a
   * person staring at the conversation still counted as away. Now returning
   * fetches at once — which also puts the newest messages on screen the
   * moment they look, and that is worth the request on its own.
   */
  useEffect(() => {
    // `active` outlives the mobile back button — it is what the desktop pane
    // reads and it is deliberately not cleared — so on a phone the URL is the
    // one that says whether a conversation is on screen worth polling.
    if (!active || (isMobile && !threadParam)) return;
    const refresh = () => {
      if (document.hidden) return;
      api
        .get(`/chat/threads/${active}`)
        .then(({ data }) =>
          setThreads((prev) =>
            prev.map((th) => (th.thread_id === active ? { ...th, ...data, unread_count: 0 } : th))
          )
        )
        .catch(() => {});
    };
    const id = setInterval(refresh, 7000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [active, isMobile, threadParam]);

  /**
   * Which conversation is on screen.
   *
   * Desktop reads `active` — whatever the sidebar last selected, which
   * deliberately does not touch the URL. A phone reads the URL, and that is the
   * whole of the back behaviour: tapping a conversation pushes `?thread=`, so
   * the browser's own back button returns to the list with no state of ours to
   * keep in step, and a push notification that arrives with `?thread=` already
   * on it opens the conversation instead of the list, as Jorge asked.
   */
  const openId = isMobile ? threadParam : active;
  const openThread = threads.find((th) => th.thread_id === openId) || null;
  // T19 X1. A cancelled job's conversation is read-only: the server refuses
  // new messages (409 `job_canceled`) and the composer is simply not drawn,
  // because the last line of the thread is the system message saying why.
  const closed = openThread?.job_status === "canceled";

  /**
   * A system line in the reader's own language (T19).
   *
   * `content` is the English sentence the server stored — what any client that
   * does not know the key still shows. `system_key` names the message, and the
   * requester's name is the one parameter it needs: the open thread carries it
   * as `client_name`; a list row does not, and there it is the other party for
   * a FixyBee and the reader themselves for the requester.
   */
  const systemText = (m, th) => {
    if (m?.system_key === "job_canceled") {
      const client = th?.client_name
        || (th?.participants?.[0] === user?.user_id
          ? user?.profile?.full_name
          : th?.other_party?.full_name)
        || "";
      return t("chatX.sysJobCanceled", { client });
    }
    return m?.content;
  };
  const showList = !isMobile || !openThread;
  const showConversation = !isMobile || !!openThread;

  /**
   * When this page IS the screen, rather than a pane inside one (T13-M1).
   *
   * The same condition `.chat-shell` is applied on below, named once so the
   * three things that depend on it cannot drift apart: the shell class, the
   * tab bar, and the keyboard.
   *
   * `isMobile` is 768 and not the bar's 992 on purpose. Between the two the
   * split renders — list and conversation side by side, ordinary page content
   * — and the bar belongs there exactly as it does on any other dashboard
   * screen. Full screen is the only state that owns the bottom of the viewport.
   */
  const fullscreenThread = isMobile && !!openThread;

  /* The regression T13-M1 exists to fix. The bar stayed over the conversation,
     and with it `body` kept the 110px it reserves for the bar — so the shell,
     which is the viewport minus the layout's chrome, ended below the fold and
     took the composer with it. The bar is hidden by the same hook the Dany
     thread uses, and `TabBarProvider` gives the padding back in the same move. */
  useHideTabBar(fullscreenThread);
  /* And the keyboard, by the same hook for the same reason — see
     hooks/useVisualViewport.js. Three things come with that one call now
     (T17-M1a): the shell's height, the shell's offset while iOS pans the
     visual viewport, and the lock that holds the document still underneath so
     the browser's auto-scroll-on-focus has nothing left to move. Gated,
     because above 768 this page is a split whose height is `md:h-[60vh]`, in
     the page's flow, with a document that is allowed to scroll. */
  useVisualViewport(fullscreenThread, anchorToBottom);

  // Whether the conversation on screen was reached from the list on this visit,
  // and therefore whether there is an entry to pop. Arriving straight from a
  // push there is not one: going "back" then has to replace, or the button
  // would walk the reader out of the app.
  const pushedFromList = useRef(false);

  const openConversation = (tid) => {
    if (!isMobile) {
      loadThread(tid);
      return;
    }
    pushedFromList.current = true;
    // The messages are fetched by the `?thread=` effect above, which is the one
    // that also has to catch the bell changing this same param. Calling
    // loadThread here as well would fetch the thread twice.
    navigate(`${location.pathname}?thread=${tid}`);
  };

  const backToList = () => {
    if (pushedFromList.current) {
      pushedFromList.current = false;
      navigate(-1);
      return;
    }
    navigate(location.pathname, { replace: true });
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !openId) return;
    setSending(true);
    try {
      // No `blocked` branch any more (T18-M4): the server stopped running the
      // anti-contact guardrail on chat, so this POST cannot come back refused.
      // Messages written BEFORE today that were blocked still exist and are
      // still drawn as blocked in the transcript below — that half is history
      // and stays.
      await api.post(`/chat/threads/${openId}/messages`, { content: text });
      setText("");
      await loadThread(openId);
    } finally { setSending(false); }
  };

  /**
   * Enter, and the one place this composer parts company with Dany's.
   *
   * **On a phone, Enter breaks the line and the button sends** (T17-M1b,
   * Jorge; the messaging pattern — WhatsApp, Messenger, every one of them).
   * There is no Shift key on a touch keyboard, so "Enter sends, Shift+Enter
   * breaks" leaves a phone with no way at all to write a second line — which
   * is the most likely reason the field looked to Jorge like it never grew:
   * every attempt to make it grow sent the message instead.
   *
   * Desktop keeps what was already decided (`ds/dany/DanyComposer.jsx:144`):
   * Enter sends, Shift+Enter breaks. Both halves are reachable there, and the
   * fast path for a keyboard is the key, not the mouse.
   *
   * The `sending` guard is the keyboard's half of the button's `disabled`:
   * without it a held Enter posts the same message twice while the first POST
   * is still in flight.
   */
  const onComposerKeyDown = (e) => {
    if (e.key !== "Enter" || isMobile || e.shiftKey) return;
    e.preventDefault();
    if (!sending) send(e);
  };

  return (
    <DashboardLayout role={role}>
      <div data-testid="chat-page">
        {/* The page title is the list screen's title. A phone showing a
            conversation shows that conversation's own header instead — the
            counterpart's name, beside the way back. */}
        <h1
          className={`font-display text-3xl md:text-4xl font-bold text-worker mb-6 ${
            showList ? "" : "hidden md:block"
          }`}
        >
          {t("chatX.title")}
        </h1>

        {threads.length === 0 ? (
          <div className="card-base p-10 text-center">
            <p className="text-smoke">{t("chatX.emptyAll")}</p>
          </div>
        ) : (
          /*
            One grid, two panes, and on a phone exactly one of them is on screen
            at a time. The height follows: the split needs a fixed box so both
            panes scroll inside it, while a phone's list is ordinary page
            content and its conversation IS the screen — `.chat-shell` in
            index.css, which since T17-M1a takes itself out of the document
            (`position: fixed`) and is sized by the visible viewport rather
            than by the page's flow minus the dashboard's chrome. It therefore
            covers DashboardLayout's top bar, which is the point: a messaging
            conversation is a screen, not a card inside one.
          */
          <div
            /* Both tracks are `minmax(0, ...)` (T13-M2). Below 768 this is a
               bare `grid` — one implicit `auto` track, floored at min-content
               — and the thread rows carry a `truncate` name and a `truncate`
               message preview, so a long last message set the width of the
               page. Above 768 the old `1fr` was `minmax(auto, 1fr)`, the same
               floor by another spelling, and the conversation header's name is
               also `truncate`. Neither overflowed with the short data this was
               written against; both would with a real inbox. */
            className={`grid grid-cols-[minmax(0,1fr)] md:grid-cols-[260px_minmax(0,1fr)] gap-3 md:h-[60vh] md:min-h-[400px] ${
              fullscreenThread ? "chat-shell" : ""
            }`}
          >
            {/* N48. `space-y-1` because the rows were stacked edge to edge:
                two conversations with long names read as one block of text,
                and the active row's tint was the only thing separating them. */}
            <div
              className={`card-base p-2 overflow-y-auto space-y-1 ${showList ? "" : "hidden md:block"}`}
              data-testid="threads-list"
            >
              {threads.map((th) => (
                <button
                  key={th.thread_id}
                  onClick={() => openConversation(th.thread_id)}
                  className={`w-full text-left p-3 rounded-sm transition-colors ${
                    openId === th.thread_id ? "bg-hive" : "hover:bg-sand"
                  }`}
                  data-testid={`thread-${th.thread_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {/* **`min-w-0` is what makes `truncate` work at all here**
                        (N48). A flex item's automatic minimum size is its own
                        content, so a long company name did not ellipsise — it
                        widened the row past the 260px column, pushed the
                        unread badge out of the card, and overlapped the row
                        beside it. The conversation header twenty lines below
                        has carried `min-w-0` on its own flex row since it was
                        written; this one never did.
                        `shrink-0` on the badge for the other half of the same
                        rule: a count is not a thing to compress. */}
                    <span className="font-display font-semibold text-worker truncate min-w-0">
                      {th.other_party?.company_name || th.other_party?.full_name || t("chatX.convFallback")}
                    </span>
                    {th.unread_count > 0 && (
                      <span className="badge bg-danger text-cream shrink-0">{th.unread_count}</span>
                    )}
                  </div>
                  <p className="text-xs text-smoke mt-1 truncate">{systemText(th.messages?.[0], th) || "—"}</p>
                </button>
              ))}
            </div>

            {/*
              `min-h-0` is load-bearing, not tidying (#18). A grid item's
              automatic minimum size is its content, so without it this card
              grew to the height of the whole conversation, pushed `.chat-shell`
              past the viewport, and handed the scrolling to the PAGE — which is
              how scrolling the messages took the header, and the way back, off
              the top of the screen. With it, the card is exactly the height of
              the shell and the three rows below divide it: header and composer
              at their natural size, messages taking the rest and scrolling
              alone.
            */}
            <div
              className={`card-base flex-col overflow-hidden min-h-0 ${
                showConversation ? "flex" : "hidden md:flex"
              }`}
            >
              {openThread ? (
                <>
                  {/*
                    The conversation's own header, pinned. `shrink-0` is what
                    keeps it there: it is the top row of a flex column whose
                    middle row is the only scroller, so the messages move under
                    it and it never does. `position: sticky` would be inert here
                    — the nearest scrollport is this card, and the card does not
                    scroll — so the pinning is the layout's, not a property's.
                  */}
                  <div className="shrink-0 px-2 py-3 md:px-4 border-b border-sand flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 md:gap-2 min-w-0">
                      {/* The way back, and only where there is somewhere to go
                          back TO: the desktop list never left the screen. */}
                      <button
                        type="button"
                        onClick={backToList}
                        className="md:hidden p-1 -ml-1 text-worker shrink-0"
                        aria-label={t("chatX.backToList")}
                        data-testid="chat-back"
                      >
                        <ChevronLeft size={22} />
                      </button>
                      <p className="font-display font-bold text-worker truncate">
                        {openThread.other_party?.company_name || openThread.other_party?.full_name}
                      </p>
                      {openThread.other_party?.is_verified && <ShieldCheck size={16} className="text-success shrink-0" />}
                    </div>
                    <p className="text-xs text-smoke font-mono shrink-0">{t("chatX.msgsCount", { n: openThread.messages?.length || 0 })}</p>
                  </div>
                  {/*
                    T20-M2. While this conversation IS the screen, the e-mail
                    verification banner is a row of the card, under the header
                    and above the messages — the global one is fixed at the
                    shell's own z-index and sat on the composer. Only here: in
                    the desktop split the page is ordinary content and the
                    floating banner is still the one shown.
                  */}
                  {fullscreenThread && <VerifyEmailBanner placement="inline" />}
                  {/*
                    `min-h-0` for the same reason as the card — a flex item does
                    not shrink below its content without it, and this one has to.
                    `overscroll-contain` stops the gesture from chaining to the
                    page when the thread hits either end, which on a phone is
                    the other way the header used to leave the screen.
                  */}
                  <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-2" data-testid="messages-area">
                    {(openThread.messages || []).map((m) => {
                      const isMe = m.sender_id === user.user_id;
                      const isSys = m.is_system || m.sender_id === "system";
                      if (isSys) {
                        return (
                          <div key={m.message_id} className="text-center text-xs text-smoke italic py-2 border-y border-dashed border-sand" data-testid={`msg-${m.message_id}`}>
                            {systemText(m, openThread)}
                          </div>
                        );
                      }
                      const blocked = m.moderation_status === "blocked";
                      return (
                        <div key={m.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                          <div className={`max-w-[75%] rounded-md px-4 py-2 ${
                            isMe ? "bg-hive text-worker" : "bg-sand text-worker"
                          } ${blocked ? "opacity-60 border border-danger" : ""}`}>
                            {blocked && <ShieldAlert size={14} className="inline mr-1 text-danger" />}
                            <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                            <p className="text-[10px] mt-1 opacity-60 font-mono">{formatTime(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/*
                    `items-end` so the button stays on the last line as the
                    field grows upward, which is the only arrangement that does
                    not move the send target while someone is typing.

                    `composer-safe` is the home indicator's cushion, and index
                    .css applies it only with the keyboard down — with the keys
                    up there is no home indicator under this edge and the inset
                    would be a strip of dead space between the field and the
                    keyboard (T17-M1a, requirement 3).

                    The form is the LAST CHILD IN NORMAL FLOW of a shell that
                    is exactly as tall as the visible viewport. That is the
                    whole of how it stays above the keyboard — no sticky, no
                    fixed, no bottom offset to keep in step with anything.
                  */}
                  {!closed && (
                  <form onSubmit={send} className="composer-safe shrink-0 border-t border-sand p-3 flex items-end gap-2" data-testid="message-form">
                    {/*
                      The wrapper is the control (T17-M1c). It carries the
                      border, the fill and the radius, and it is the only thing
                      that paints focus — `:focus-within`, in index.css, shared
                      with Dany's bar and Dany's composer. The field inside
                      carries nothing: `.input-base` used to put a second,
                      shadowed ring on it, which is the glow Jorge photographed.
                    */}
                    <div className="composer-shell flex-1 min-w-0 rounded-sm bg-paper px-4 py-2.5">
                      <textarea
                        ref={inputRef}
                        rows={1}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        /* The fit rides the DOM event as well as the render.
                           The effect below covers the changes no keystroke
                           makes — a send clearing the box, a thread switching
                           — and this covers the frame the person is looking at,
                           without waiting on React to schedule anything. */
                        onInput={(e) => fitComposer(e.currentTarget)}
                        onKeyDown={onComposerKeyDown}
                        /* So the touch keyboard offers a return key rather than
                           a Send it no longer honours. */
                        enterKeyHint={isMobile ? "enter" : "send"}
                        className="composer-autogrow block w-full resize-none border-0 bg-transparent p-0 font-body text-worker placeholder:text-smoke-light leading-normal overflow-y-auto"
                        placeholder={t("chatX.messagePh")} maxLength={2000}
                        data-testid="message-input"
                      />
                    </div>
                    {/*
                      Charcoal, not hive. The gold circle beside a chat field is
                      the Dany FAB (components/dany/DanyFab.jsx, --action-primary),
                      and a second gold circle 60px away asking to be pressed for
                      something else is the confusion Daniel reported. Dark is
                      the palette's other action surface — `.badge-featured` is
                      already `bg-worker`, and --surface-inverse names it — so
                      this is a token that exists, not a new colour.
                    */}
                    <button
                      type="submit"
                      disabled={sending || !text.trim()}
                      className="shrink-0 inline-flex items-center justify-center rounded-sm bg-worker px-4 py-3 text-canvas shadow-soft transition-all duration-200 hover:bg-charcoal hover:-translate-y-0.5 hover:shadow-medium active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none ring-hive"
                      aria-label={t("chatX.send")}
                      data-testid="send-message"
                    >
                      <Send size={18} />
                    </button>
                  </form>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-smoke">{t("chatX.pickConv")}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
